import BaseView from "../../../js/base-view";
import splitButtonTextIntoLetters from "../../../js/utils/splitTextIntoLetters";

export default class Navigation extends BaseView {
	/**
	 * Entrypoint
	 */
	init() {
		this.initMobile();
		this.initDesktopKeyboard();
		this.preserveQueryParams();
	}

	initMobile() {
		this.menuToggle = this.el.querySelector(".navigation__mobile-toggle");
		this.drawer = this.el.querySelector("[data-drawer]");
		this.mobileOverlay = this.el.querySelector(".navigation__mobile-overlay");
		this.drawerStack = [];
		this.previousFocus = null;

		this.updateActiveLanguage();
		this.setupMobileMenuAnimation();

		if (this.menuToggle && this.drawer) {
			this.setupAccessibility();
			this.bindMobileEvents();
		}
	}

	setupMobileMenuAnimation() {
		const mobileMenu = this.el.querySelector(".navigation__mobile-menu");
		if (!mobileMenu) return;

		const primaryItems = mobileMenu.querySelectorAll(
			".navigation__mobile-menu__primary .navigation__mobile-menu__item",
		);
		const primaryCount = primaryItems.length;

		const itemDelay = 0.05;
		const itemDuration = 0.5;
		const initialDelay = 0.1;
		const gapBetweenMenus = 0.1;
		const secondaryStartDelay =
			initialDelay + primaryCount * itemDelay + gapBetweenMenus;

		mobileMenu.style.setProperty("--menu-item-delay", `${itemDelay}s`);
		mobileMenu.style.setProperty("--menu-item-duration", `${itemDuration}s`);
		mobileMenu.style.setProperty("--menu-primary-count", primaryCount);
		mobileMenu.style.setProperty(
			"--menu-secondary-start-delay",
			`${secondaryStartDelay}s`,
		);
		mobileMenu.style.setProperty("--menu-initial-delay", `${initialDelay}s`);
	}

	resetMobileMenuAnimation() {
		const mobileMenu = this.el.querySelector(".navigation__mobile-menu");
		if (!mobileMenu) return;

		const allItems = mobileMenu.querySelectorAll(
			".navigation__mobile-menu__item",
		);

		allItems.forEach((item) => {
			item.style.opacity = "0";
			item.style.transform = "translateY(-8px)";
		});

		requestAnimationFrame(() => {
			allItems.forEach((item) => {
				item.style.removeProperty("opacity");
				item.style.removeProperty("transform");
			});
		});
	}

	updateActiveLanguage() {
		const languageLinks = Array.from(
			this.el?.querySelectorAll(".navigation__mobile-menu__languages a") || [],
		);
		const currentUrl = window.location.href;
		const currentUrlObj = new URL(currentUrl);

		// Normalize pathname (remove trailing slash for comparison)
		const normalizePath = (path) => {
			return path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;
		};
		const currentPath = normalizePath(currentUrlObj.pathname);

		languageLinks.forEach((link) => {
			const linkUrl = new URL(link.href);
			const linkPath = normalizePath(linkUrl.pathname);

			const isActive = linkPath === currentPath;

			if (isActive) {
				link.classList.add("is-active");
				link.setAttribute("aria-current", "page");
			} else {
				link.classList.remove("is-active");
				link.removeAttribute("aria-current");
			}
		});
	}

	setupAccessibility() {
		this.toggleLabel = this.el.querySelector('label[for="mobile-menu-toggle"]');

		if (this.toggleLabel) {
			this.toggleLabel.setAttribute("aria-expanded", "false");
		}

		if (this.mobileOverlay) {
			this.mobileOverlay.setAttribute("aria-hidden", "true");
		}

		if (this.drawer) {
			this.drawer.setAttribute("aria-hidden", "true");
		}

		this.ariaLiveRegion = this.el.querySelector(".navigation__aria-live");
		if (this.ariaLiveRegion) {
			this.ariaLiveRegion.setAttribute("aria-live", "polite");
			this.ariaLiveRegion.setAttribute("aria-atomic", "true");
		}
	}

	bindMobileEvents() {
		this.on("click", "[data-has-submenu]", this.openDrawer.bind(this));
		this.on("click", "[data-drawer-back]", this.closeDrawer.bind(this));
		this.on("click", "[data-search-toggle]", () => {
			if (this.menuToggle && this.menuToggle.checked) {
				this.closeMenu();
			}
		});
		this.on(
			"click",
			".navigation__mobile-overlay a:not([data-has-submenu])",
			(event) => {
				if (
					this.menuToggle &&
					this.menuToggle.checked &&
					!event.target.closest(".navigation__mobile-menu__languages")
				) {
					this.closeMenu();
				}
			},
		);

		this.on("click", ".navigation__mobile-menu__languages a", (event) => {
			if (this.menuToggle && this.menuToggle.checked) {
				this.closeMenu();
			}
		});

		if (this.menuToggle) {
			if (this.menuToggle.checked) {
				this.menuToggle.checked = false;
				document.body.classList.remove("menu-open");
			}
			this.menuToggle.addEventListener("change", (e) => {
				const isOpen = e.target.checked;
				document.body.classList.toggle("menu-open", isOpen);

				if (this.toggleLabel) {
					this.toggleLabel.setAttribute("aria-expanded", isOpen.toString());
				}

				if (this.mobileOverlay) {
					this.mobileOverlay.setAttribute("aria-hidden", (!isOpen).toString());
				}

				if (this.ariaLiveRegion) {
					this.ariaLiveRegion.textContent = isOpen
						? "Menu de navigation ouvert"
						: "Menu de navigation fermé";
				}

				if (isOpen) {
					this.previousFocus = document.activeElement;
					this.trapFocus(this.mobileOverlay);
					const firstMenuItem = this.mobileOverlay.querySelector(
						".navigation__mobile-menu__item__link",
					);
					if (firstMenuItem) {
						setTimeout(() => {
							firstMenuItem.focus();
						}, 100);
					}
				} else {
					this.releaseFocus();
					if (this.previousFocus) {
						this.previousFocus.focus();
					}
					this.closeAllDrawers();
					this.resetMobileMenuAnimation();
				}
			});
		}

		this.on(
			"keydown",
			null,
			(e) => {
				if (e.key === "Escape") {
					if (this.drawerStack.length > 0) {
						this.closeDrawer(e);
					} else if (this.menuToggle && this.menuToggle.checked) {
						this.menuToggle.checked = false;
						this.menuToggle.dispatchEvent(new Event("change"));
					}
				}
			},
			true,
		);
	}

	openDrawer(event) {
		event.preventDefault();

		const target = event.target.dataset.hasSubmenu
			? event.target
			: event.target.closest("[data-has-submenu]");

		if (!target || !target.dataset.submenu) return;

		try {
			const menuData = JSON.parse(target.dataset.submenu);

			if (!this.drawer) return;

			target.setAttribute("aria-expanded", "true");

			this.populateDrawer(
				this.drawer,
				menuData,
				target.dataset.menuTitle || "Menu",
			);
			this.drawerStack.push({ data: menuData, trigger: target });
			this.drawer.classList.add("is-active");
			this.drawer.setAttribute("aria-hidden", "false");

			const backButton = this.drawer.querySelector(
				".navigation__mobile-drawer__back",
			);
			if (backButton) {
				setTimeout(() => {
					backButton.focus();
				}, 100);
			}
		} catch (error) {
			console.error("Error parsing submenu data:", error);
		}
	}

	closeDrawer(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (this.drawerStack.length === 0) return;

		const drawerInfo = this.drawerStack.pop();

		if (drawerInfo.trigger) {
			drawerInfo.trigger.setAttribute("aria-expanded", "false");
		}

		if (this.drawer) {
			this.drawer.classList.remove("is-active");
			this.drawer.setAttribute("aria-hidden", "true");
		}

		if (drawerInfo.trigger) {
			setTimeout(() => {
				drawerInfo.trigger.focus();
			}, 100);
		}
	}

	closeAllDrawers() {
		this.drawerStack.forEach((drawerInfo) => {
			if (drawerInfo.trigger) {
				drawerInfo.trigger.setAttribute("aria-expanded", "false");
			}
		});
		this.drawerStack = [];
		if (this.drawer) {
			this.drawer.classList.remove("is-active");
			this.drawer.setAttribute("aria-hidden", "true");
		}
	}

	closeMenu() {
		this.closeAllDrawers();
		if (this.menuToggle) {
			this.menuToggle.checked = false;
			this.menuToggle.dispatchEvent(new Event("change"));
		}
		this.resetMobileMenuAnimation();
	}

	populateDrawer(drawer, menuData, menuTitle) {
		const content = drawer.querySelector(".navigation__mobile-drawer__content");
		if (!content) return;

		let html = "";

		menuData.forEach((item) => {
			if (item.subitems && item.subitems.length > 0) {
				html += this.renderDrawerCategoryParent(item);
				html += '<ul class="navigation__mobile-drawer__category-children">';
				item.subitems.forEach((subitem) => {
					html += `<li class="navigation__mobile-drawer__item">${this.renderDrawerItem(
						subitem,
					)}</li>`;
				});
				html += "</ul>";
			} else {
				html += this.renderDrawerItem(item);
			}
		});

		content.innerHTML = html;
	}

	renderDrawerCategoryParent(item) {
		const link = item.link && item.link !== false ? item.link : "#";
		const title = item.title || "";
		const isExternal = item.isExternal || false;

		return `<a
			href="${link}"
			class="navigation__mobile-drawer__category-parent"
			${isExternal ? 'target="_blank"' : ""}
		>${this.escapeHtml(title)}</a>`;
	}

	renderDrawerItem(item) {
		const link = item.link && item.link !== false ? item.link : "#";
		const title = item.title || "";
		const isExternal = item.isExternal || false;

		return `<a
			href="${link}"
			class="navigation__mobile-drawer__item__link"
			${isExternal ? 'target="_blank"' : ""}
		>${this.escapeHtml(title)}</a>`;
	}

	escapeHtml(text) {
		const textarea = document.createElement("textarea");
		textarea.innerHTML = text;
		return textarea.value;
	}

	trapFocus(container) {
		if (!container) return;

		const focusableElements = container.querySelectorAll(
			'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
		);

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		const handleTabKey = (e) => {
			if (e.key !== "Tab") return;

			if (e.shiftKey) {
				if (document.activeElement === firstElement) {
					e.preventDefault();
					lastElement.focus();
				}
			} else {
				if (document.activeElement === lastElement) {
					e.preventDefault();
					firstElement.focus();
				}
			}
		};

		container.addEventListener("keydown", handleTabKey);
		this.focusTrapHandler = handleTabKey;
	}

	releaseFocus() {
		if (this.mobileOverlay && this.focusTrapHandler) {
			this.mobileOverlay.removeEventListener("keydown", this.focusTrapHandler);
			this.focusTrapHandler = null;
		}
	}

	initDesktopKeyboard() {
		this.addDesktopKeyboardStyles();
		this.submenuCloseTimeouts = new Map();

		const submenuItems = this.el.querySelectorAll(
			".navigation__primary .navigation__list__item--submenu.is-first-level, .navigation__secondary .navigation__list__item--submenu.is-first-level",
		);

		submenuItems.forEach((item) => {
			const link = item.querySelector(".navigation__list__item__title");
			const submenu = item.querySelector(
				".navigation__list__submenu, .navigation__list__submenu--level-1, .navigation__list__submenu-list",
			);

			if (!link || !submenu) return;

			link.setAttribute("aria-expanded", "false");
			link.setAttribute("aria-haspopup", "true");

			link.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					this.toggleDesktopSubmenu(item, link, submenu, true);
				} else if (e.key === "Escape") {
					this.closeDesktopSubmenu(item, link, submenu);
				} else if (e.key === "ArrowDown") {
					e.preventDefault();
					if (!item.classList.contains("is-open")) {
						this.openDesktopSubmenu(item, link, submenu, true);
					}
					const firstSubmenuLink = submenu.querySelector("a");
					if (firstSubmenuLink) {
						firstSubmenuLink.focus();
					}
				}
			});

			item.addEventListener("mouseenter", () => {
				const timeoutId = this.submenuCloseTimeouts.get(item);
				if (timeoutId) {
					clearTimeout(timeoutId);
					this.submenuCloseTimeouts.delete(item);
				}
				this.openDesktopSubmenu(item, link, submenu, false);
			});

			item.addEventListener("mouseleave", () => {
				const timeoutId = setTimeout(() => {
					this.closeDesktopSubmenu(item, link, submenu);
					this.submenuCloseTimeouts.delete(item);
				}, 300);
				this.submenuCloseTimeouts.set(item, timeoutId);
			});

			submenu.addEventListener("mouseenter", () => {
				const timeoutId = this.submenuCloseTimeouts.get(item);
				if (timeoutId) {
					clearTimeout(timeoutId);
					this.submenuCloseTimeouts.delete(item);
				}
			});

			submenu.addEventListener("mouseleave", () => {
				const timeoutId = setTimeout(() => {
					this.closeDesktopSubmenu(item, link, submenu);
					this.submenuCloseTimeouts.delete(item);
				}, 300);
				this.submenuCloseTimeouts.set(item, timeoutId);
			});

			item.addEventListener("focusout", (e) => {
				if (!item.contains(e.relatedTarget)) {
					this.closeDesktopSubmenu(item, link, submenu);
				}
			});
		});
	}

	openDesktopSubmenu(item, link, submenu, isKeyboard = false) {
		item.classList.add("is-open");
		link.setAttribute("aria-expanded", "true");
		if (isKeyboard) {
			link.focus();
		}
	}

	closeDesktopSubmenu(item, link, submenu) {
		item.classList.remove("is-open");
		link.setAttribute("aria-expanded", "false");
		link.blur();
	}

	toggleDesktopSubmenu(item, link, submenu, isKeyboard = false) {
		if (item.classList.contains("is-open")) {
			this.closeDesktopSubmenu(item, link, submenu);
		} else {
			this.openDesktopSubmenu(item, link, submenu, isKeyboard);
		}
	}

	addDesktopKeyboardStyles() {
		if (document.getElementById("navigation-keyboard-styles")) return;

		const style = document.createElement("style");
		style.id = "navigation-keyboard-styles";
		style.textContent = `
			.navigation__list__item--submenu.is-open > .navigation__list__submenu--level-1,
			.navigation__list__item--submenu.is-open .navigation__list__submenu--level-1 {
				opacity: 1 !important;
				visibility: visible !important;
				transform: scaleY(1) !important;
			}
			.navigation__list__item--submenu.is-open .navigation__list__submenu-list .navigation__list__item,
			.navigation__list__item--submenu.is-open > .navigation__list__submenu--level-1 > .navigation__list__item {
				opacity: 1 !important;
				transform: translateY(0) !important;
			}
		`;
		document.head.appendChild(style);
	}

	// --------------------------------
	// #region My program query params
	// --------------------------------

	preserveQueryParams() {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has("festival_edition")) {
			const festivalEdition = urlParams.get("festival_edition");
			const languageLinks = this.el.querySelectorAll(".menu-link--language");
			languageLinks.forEach((link) => {
				link.href += `?festival_edition=${festivalEdition}`;
			});
		}
	}

	// --------------------------------
	// #endregion
	// --------------------------------

	/**
	 * Bind component's events
	 */
	bindEvents() {}

	// --------------------------------
	// #region Event Handlers
	// --------------------------------

	// --------------------------------
	// #endregion
	// --------------------------------

	/**
	 * Before the component gets destroyed
	 * - unbind any event not bound with this.on()
	 * - reset UI if needed (any classes/attributes added?)
	 */
	beforeDestroy() {
		this.releaseFocus();
		if (this.menuToggle) {
			this.menuToggle.checked = false;
			document.body.classList.remove("menu-open");
		}
		this.closeAllDrawers();
		if (this.submenuCloseTimeouts) {
			this.submenuCloseTimeouts.forEach((timeoutId) => {
				clearTimeout(timeoutId);
			});
			this.submenuCloseTimeouts.clear();
		}
	}
}
